import React from 'react';
import PropTypes from 'prop-types';

// Helper function to generate lighter shade of color
const getLighterColor = (color) => {
  if (color === '#003D7A') return '#4A90E2';
  if (color === '#4A90E2') return '#8BA3B8';
  return color;
};

const ProgressBar = ({ label, value, total, description, color = '#003D7A' }) => {
  // Calculate percentage
  const percentage = total > 0 ? (value / total) * 100 : 0;

  // Format currency
  const formatCurrency = (val) => {
    if (isNaN(val)) return '0 kr';
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' })
      .format(val)
      .replace('kr', '')
      .trim();
  };

  const lighterColor = getLighterColor(color);

  return (
    <div className="progress-bar-container mb-4">
      {/* Label Section */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h6 style={{
            fontSize: '0.95rem',
            fontWeight: '600',
            color: '#2c3e50',
            marginBottom: '0.25rem',
          }}>
            {label}
          </h6>
          {description && (
            <small style={{
              fontSize: '0.75rem',
              color: '#6c757d',
              display: 'block',
              marginTop: '0.25rem',
            }}>
              {description}
            </small>
          )}
        </div>
        <div className="text-end">
          <div style={{
            fontSize: '1.1rem',
            fontWeight: '700',
            color: '#003D7A',
            marginBottom: '0.25rem',
          }}>
            {formatCurrency(value)} kr
          </div>
          <div style={{
            fontSize: '0.85rem',
            fontWeight: '600',
            color: color,
          }}>
            {percentage.toFixed(0)}%
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div
        style={{
          backgroundColor: '#F0F2F5',
          borderRadius: '6px',
          height: '32px',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <div
          style={{
            background: `linear-gradient(90deg, ${color} 0%, ${lighterColor} 100%)`,
            height: '100%',
            width: `${percentage}%`,
            borderRadius: '6px',
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingRight: '12px',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Shine effect */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '50%',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 100%)',
              pointerEvents: 'none',
            }}
          ></div>

          {percentage > 15 && (
            <span
              style={{
                color: 'white',
                fontSize: '0.8rem',
                fontWeight: '700',
                position: 'relative',
                zIndex: 1,
                textShadow: '0 1px 2px rgba(0,0,0,0.1)',
              }}
            >
              {percentage.toFixed(0)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

ProgressBar.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  description: PropTypes.string,
  color: PropTypes.string,
};

ProgressBar.defaultProps = {
  description: '',
  color: '#003D7A',
};

export default ProgressBar;
